import { useQuery } from "@tanstack/react-query";
import { Application } from "shared/types";
import api from "api/landlord/address";
import { useCallback, useState, useEffect, useRef } from "react";
import { DecisionAction } from "shared/types/application";
import { Template } from "types/landlord/verificationTemplate";
import { Views, FormData, FormDataErrors } from "./types";
import {
  AdverseActionLetterType,
  UnlistUnitTiming
} from "api/landlord/templates";
import { RuleSetDecision } from "shared/app/VerificationTemplate/slices/DecisionCriteria/slices/RuleCriteria/types";
import { Reasons } from "types/application";
import { conditionKeysMap } from "./constants";
import { useSettingsController } from "views/settings-integration/controller";
import { ProspectsCreationOptions } from "views/settings-integration/integrations/pms/constants";
import {
  getIntegrationCredentials,
  QUERY_INTEGRATION_CREDENTIALS
} from "api/landlord/integrations";
import { getFlow } from "./utils";

export const useMakeDecisionController = (applicationId: string) => {
  const [view, setView] = useState<Views>(Views.MakeDecision);
  const [formData, setFormData] = useState<FormData>({
    decisionNotes: "",
    unlistUnit: true,
    disallowApplications: true,
    sendNotifications: false,
    addApplicantsToPMS: false,
    conditions: [],
    residencyConditions: [],
    additionalCondition: "",
    additionalEmailText: "",
    additionalResidencyCondition: "",
    increasedMonthlyRent: ""
  });
  const [formDataErrors, setFormDataErrors] = useState<FormDataErrors>({
    increasedMonthlyRent: ""
  });

  const { currentSettings } = useSettingsController();
  const { pms: pmsSettings } = currentSettings;

  const { data: credentialsData } = useQuery({
    queryKey: [QUERY_INTEGRATION_CREDENTIALS],
    queryFn: getIntegrationCredentials
  });

  const updateFormData = useCallback(
    (updates: Partial<FormData>) => {
      setFormData((prev) => ({
        ...prev,
        ...updates
      }));

      // Clear corresponding errors for updated keys
      const clearedErrors: Partial<FormDataErrors> = {};
      for (const key of Object.keys(updates) as (keyof FormData)[])
        if (formDataErrors[key]) clearedErrors[key] = "";

      setFormDataErrors((prev) => ({
        ...prev,
        ...clearedErrors
      }));
    },
    [formDataErrors]
  );

  const applicationMeta = useQuery<
    Application & {
      verification_template: Template;
      score: number;
      rule_reasons?: Reasons[];
    }
  >({
    queryKey: ["APPLICATION", applicationId],
    queryFn: () =>
      api.getApplicationById(applicationId || "").then((response) => {
        const res = response as unknown as Application & {
          verification_template: Template;
          score: number;
          rule_reasons?: Reasons[];
        };

        const timing =
          res.verification_template.automatically_unlist_unit_timing;

        // Default toggle values based on timing setting and decision type:
        // - ON_FIRST_APPLICATION: toggles will be hidden (values don't matter)
        // - ON_APPROVE: true unless recommendation is explicitly "decline"
        // - NEVER: false for all decisions
        const shouldPreCheckToggles =
          timing === UnlistUnitTiming.ON_APPROVE &&
          res.recommendation !== DecisionAction.Decline;

        setFormData((prev) => ({
          ...prev,
          conditions: res.rule_reasons
            ? getTransformedConditions(res.rule_reasons, res.recommendation)
            : [],
          decision: res.recommendation as DecisionAction,
          unlistUnit: shouldPreCheckToggles,
          disallowApplications: shouldPreCheckToggles,
          sendNotifications: res.verification_template.notify_applicants
        }));
        return res;
      }),
    enabled: !!applicationId
  });

  // Track previous decision to detect user-initiated changes
  const prevDecisionRef = useRef<DecisionAction | undefined>(formData.decision);

  const property = applicationMeta.data?.property;
  const ruleReasons = applicationMeta.data?.rule_reasons;
  const propertyHasPmsLink = Boolean(property?.pms_id || property?.external_id);

  const propertyCredentialId = property?.integration_credential?.id ?? null;
  const propertyCredential = propertyCredentialId
    ? credentialsData?.integration_credentials.find(
        (c) => c.id === propertyCredentialId
      )
    : undefined;

  const effectiveTrigger =
    propertyCredential?.settings?.pms_prospect_creation_trigger ??
    pmsSettings.pms_prospect_creation_trigger;
  const isDecisionTriggeredPmsPush =
    effectiveTrigger !== ProspectsCreationOptions.Submission &&
    effectiveTrigger !== ProspectsCreationOptions.Start;

  // Reset decision-dependent fields when the user changes their decision.
  // Toggle defaults derive from verification template timing and PMS integration
  // settings so they reflect the partner's configuration for the chosen decision.
  useEffect(() => {
    const timing =
      applicationMeta.data?.verification_template
        .automatically_unlist_unit_timing;
    const prevDecision = prevDecisionRef.current;
    const newDecision = formData.decision;

    if (!newDecision) return;

    const decisionChanged = prevDecision !== newDecision;
    prevDecisionRef.current = newDecision;

    const updates: Partial<FormData> = {};

    if (timing === UnlistUnitTiming.ON_APPROVE) {
      const shouldCheck = newDecision !== DecisionAction.Decline;
      if (decisionChanged) {
        updates.unlistUnit = shouldCheck;
        updates.disallowApplications = shouldCheck;
      }
    }

    const settingKey = `pms_prospect_creation_on_${newDecision}`;
    const rawPmsSetting =
      propertyCredential?.settings?.[settingKey] ?? pmsSettings[settingKey];
    const pmsDefault =
      propertyHasPmsLink && isDecisionTriggeredPmsPush
        ? rawPmsSetting === true || rawPmsSetting === "true"
        : false;
    updates.addApplicantsToPMS = pmsDefault;

    if (decisionChanged && ruleReasons) {
      updates.conditions = getTransformedConditions(ruleReasons, newDecision);
      updates.additionalCondition = "";
      updates.additionalEmailText = "";
      updates.residencyConditions = [];
      updates.additionalResidencyCondition = "";
      updates.increasedMonthlyRent = "";
    }

    if (Object.keys(updates).length)
      setFormData((prev) => ({ ...prev, ...updates }));
  }, [
    formData.decision,
    applicationMeta.data?.verification_template
      .automatically_unlist_unit_timing,
    propertyHasPmsLink,
    isDecisionTriggeredPmsPush,
    pmsSettings.pms_prospect_creation_on_approve,
    pmsSettings.pms_prospect_creation_on_conditional_approve,
    pmsSettings.pms_prospect_creation_on_decline,
    propertyCredential?.settings?.pms_prospect_creation_on_approve,
    propertyCredential?.settings?.pms_prospect_creation_on_conditional_approve,
    propertyCredential?.settings?.pms_prospect_creation_on_decline,
    ruleReasons
  ]);

  const flow = getFlow(
    formData.decision,
    applicationMeta.data?.verification_template,
    formData.sendNotifications
  );

  const setNextView = () => {
    const currentViewIndex = flow.indexOf(view);
    if (currentViewIndex == 0)
      resetConditions(
        applicationMeta.data?.rule_reasons,
        applicationMeta.data?.recommendation
      );
    setView(flow[currentViewIndex + 1]);
  };

  const setPrevView = () => {
    const currentViewIndex = flow.indexOf(view);
    setView(flow[currentViewIndex - 1]);
  };

  const validateFormData = () => {
    if (
      formData.residencyConditions?.includes("increased_monthly_rent") &&
      !formData.increasedMonthlyRent
    ) {
      setFormDataErrors((prev) => ({
        ...prev,
        increasedMonthlyRent: "Please enter an amount"
      }));
      return false;
    }

    return true;
  };

  const getTransformedConditions = (
    ruleReasons: Reasons[],
    recommendation: DecisionAction | undefined
  ) => {
    if (
      applicationMeta.data?.recommendation !== formData.decision ||
      applicationMeta.data?.verification_template.adverse_action_letter_type ===
        AdverseActionLetterType.Washington
    )
      return [];

    const ruleSetType =
      recommendation === DecisionAction.ConditionalApprove
        ? RuleSetDecision.Approve
        : RuleSetDecision.Decline;

    return ruleReasons.reduce<string[]>((result, ruleSet) => {
      if (ruleSet.rule_set_type !== ruleSetType) return result;

      return result.concat(
        ruleSet.rules.reduce<string[]>((fields, rule) => {
          if (
            rule.result ===
            (recommendation !== DecisionAction.ConditionalApprove)
          ) {
            const condition = conditionKeysMap[rule.field_name];
            if (condition) fields.push(condition);
          }

          return fields;
        }, [])
      );
    }, []);
  };

  const resetConditions = (
    ruleReasons: Reasons[] | undefined,
    recommendation: DecisionAction | undefined
  ) => {
    if (ruleReasons)
      setFormData((prev) => ({
        ...prev,
        conditions: getTransformedConditions(ruleReasons, recommendation)
      }));
  };

  const onNextView = () => {
    if (!validateFormData()) return;
    setNextView();
  };

  return {
    applicationMeta,
    formData,
    updateFormData,
    onNextView: onNextView,
    setPrevView,
    view,
    flow,
    resetConditions,
    validateFormData,
    formDataErrors,
    propertyCredential,
    isDecisionTriggeredPmsPush
  };
};
